import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormikHelpers } from 'formik'
import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import { Section } from 'src/tailwind/components/Section'
import BackButton from 'src/views/components/BackButton'
import { Form } from 'src/views/components/forms/formik/Form'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import { FormContent } from './FormContent'
import { programToFormValues, valuesToSaveBody } from './formMapping'
import type { FormValues } from './types'

export default function AdministratorProgramPage(): JSX.Element {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('program:programme')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEditPage = id != null && id !== 'add'
  const headers = useAuthenticatedHeaders()
  const [successfullySubmited, setSuccessfullySubmited] = React.useState(false)

  const breadcrumbsItems = [
    { page: t('program:programs'), path: `/${locale}/administrator/administration/programs` },
    {
      page: t('program:programme'),
      path: `/${locale}/administrator/administration/programs/${id ?? ''}`,
    },
  ]

  const { data: program, isLoading: isProgramLoading } = useApi({
    endpoint: Api.getAdministratorProgram,
    params: React.useMemo(() => ({ headers, args: { id: id! } }), [headers, id]),
    shouldFetch: isEditPage,
  })

  const initialValues = React.useMemo(() => programToFormValues(program), [program])

  const onSubmit = React.useCallback(
    async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
      isEditPage
        ? await Api.postAdministratorProgramsUpdate({
            headers,
            args: { id: id! },
            body: valuesToSaveBody(values),
          })
        : await Api.postAdministratorProgramsCreate({
            headers,
            body: valuesToSaveBody(values),
          })
      formikHelpers.resetForm()
      toast.success(t('program:successfully_updated_program'))
      setSuccessfullySubmited(true)
    },
    [headers, id, isEditPage, t]
  )

  React.useEffect(() => {
    if (successfullySubmited) {
      navigate(`/${locale}/administrator/administration/programs`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successfullySubmited])

  return (
    <Section
      title={t('program:programme')}
      breadcrubms={breadcrumbsItems}
      icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faList} />}
      rightElement={<BackButton link={`/${locale}/administrator/administration/programs`} />}
    >
      <Form initialValues={initialValues} onSubmit={onSubmit} enableReinitialize isConfirmable={!successfullySubmited}>
        <FormContent programTestTypes={program?.testTypes} isProgramLoading={isProgramLoading} />
      </Form>
    </Section>
  )
}
